import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactElement,
  Fragment,
} from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/lib/hooks/redux";

import { DateOfBirthFieldConfig } from "@evergiving/common/dist/es2015/form";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { RootState } from "@/lib/redux/types";
import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { FieldError } from "@/lib/components/FieldError";
import {
  expandYear,
  calendarToGregorian,
  gregorianToCalendar,
} from "@/lib/misc/dates";
import { useShowErrors } from "../hooks/useShowErrors";

type DMY = "day" | "month" | "year";

const withDateField = (value: string, dmy: DMY, fieldValue: string) => {
  const components = value.split("-");

  components[{ day: 2, month: 1, year: 0 }[dmy]] = fieldValue;
  return components.join("-");
};

const regexps = {
  day: /(?:0[1-9]|[12][0-9]|3[01]|[1-9][.-/\\ ])/,
  month: /(?:0[1-9]|1[0-2]|[1-9][.-/\\ ])/,
  year: /\D{1,4}/,
};

export function DateOfBirthField({
  config,
  locale,
}: {
  config: DateOfBirthFieldConfig;
  locale: string;
}) {
  const name = "dateOfBirth";
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useSelector((state: RootState) => state[name].value);
  const [currentValue, setCurrentValue] = useState<string>(
    gregorianToCalendar(valueFromStore, config.calendar)
  );
  const fieldValidationErrors = useFieldValidationErrors(config);
  const anyErrors = useShowErrors(config, name, currentValue);

  const dispatch = useAppDispatch();
  const errorId = `${name}-errors`;
  const currentValueSplit = currentValue.split("-");
  const [isDirty, setIsDirty] = useState(false);

  const dayField = useRef<HTMLInputElement>(null);
  const monthField = useRef<HTMLInputElement>(null);
  const yearField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentValue(gregorianToCalendar(valueFromStore, config.calendar));
  }, [config.calendar, valueFromStore]);

  const intlFormat = useMemo(() => new Intl.DateTimeFormat(locale), [locale]);

  const orderedFields: Intl.DateTimeFormatPart[] = useMemo(
    () => intlFormat.formatToParts(),
    [intlFormat]
  );

  const orderedDMY: DMY[] = orderedFields
    .map((part) => part.type)
    .filter((t): t is DMY => t === "day" || t === "month" || t === "year");

  const nextField = useCallback(
    (field: DMY): DMY | undefined => {
      const fieldOrder = orderedDMY.indexOf(field);
      if (fieldOrder < 0) {
        return undefined;
      }
      return orderedDMY[fieldOrder + 1];
    },
    [orderedDMY]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const refs = {
        day: dayField,
        month: monthField,
        year: yearField,
      };

      const { value } = event.target;
      const dmy = event.target.getAttribute("data-dmy");
      if (dmy !== "day" && dmy !== "month" && dmy !== "year") {
        throw new Error("Invalid data-dmy value");
      }

      const newValue = withDateField(
        currentValue,
        dmy,
        value.replace(/\D/g, "")
      );
      if (newValue !== currentValue) {
        setCurrentValue(newValue);
        dispatch(resetFormErrors(name));
      }

      const nextFieldName = nextField(dmy);
      const nextFieldEl = nextFieldName && refs[nextFieldName]?.current;
      if (value.match(regexps[dmy]) && nextFieldEl) {
        nextFieldEl.focus();
        nextFieldEl.select();
      }
    },

    [currentValue, nextField, dispatch]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const { current: yearFieldEl } = yearField;
      const { current: monthFieldEl } = monthField;
      const { current: dayFieldEl } = dayField;

      if (
        yearFieldEl &&
        monthFieldEl &&
        dayFieldEl &&
        monthFieldEl.value.length === 0 &&
        yearFieldEl.value.length === 0 &&
        dayFieldEl.value.length === 0
      ) {
        const pastedText = event.clipboardData.getData("text/plain");
        const match = pastedText.match(/\s*(\d+)[ .-/\\](\d+)[ .-/\\](\d+)\s*/);
        if (match) {
          let value = "--";
          for (let i = 0; i < 3; ++i) {
            const fieldName = orderedDMY[i];
            if (fieldName) {
              const fieldValue = match[i + 1];
              value = withDateField(
                value,
                fieldName,
                fieldName === "year"
                  ? expandYear(fieldValue, "past", config.calendar)
                  : fieldValue
              );
            }
          }
          setCurrentValue(value);
          yearFieldEl.focus();
          event.preventDefault();
        }
      }
    },
    [config.calendar, orderedDMY]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { current: dayFieldEl } = dayField;
      const { current: monthFieldEl } = monthField;
      const { current: yearFieldEl } = yearField;

      if (
        dayFieldEl &&
        monthFieldEl &&
        yearFieldEl &&
        event.relatedTarget !== dayFieldEl &&
        event.relatedTarget !== monthFieldEl &&
        event.relatedTarget !== yearFieldEl
      ) {
        const dayValue = dayFieldEl.value;
        const monthValue = monthFieldEl.value;
        const yearValue = yearFieldEl.value;

        const allFieldsEmpty =
          monthFieldEl.value.length === 0 &&
          yearFieldEl.value.length === 0 &&
          dayFieldEl.value.length === 0;

        if (allFieldsEmpty && !required) {
          dispatch(setFormValue(name, "", false));
          dispatch(resetFormErrors(name));
          setIsDirty(false);
        } else {
          const newValue = calendarToGregorian(
            `${expandYear(
              yearValue,
              "past",
              config.calendar
            )}-${monthValue}-${dayValue}`,
            config.calendar
          );
          dispatch(
            setFormValue(
              name,
              newValue,
              fieldValidationErrors.forValue(newValue).length > 0
            )
          );
          setIsDirty(true);
        }
      }
    },
    [config.calendar, dispatch, fieldValidationErrors, required]
  );

  const fields = new Map<string, ReactElement>([
    [
      "day",
      <input
        name="dobday"
        value={currentValueSplit[2] || ""}
        placeholder={config.dayPlaceholderText}
        spellCheck="false"
        autoComplete="bday-day"
        inputMode="numeric"
        size={2}
        maxLength={2}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        ref={dayField}
        data-dmy="day"
      />,
    ],
    [
      "month",
      <input
        name="dobmonth"
        value={currentValueSplit[1] || ""}
        size={2}
        maxLength={2}
        placeholder={config.monthPlaceholderText}
        spellCheck="false"
        autoComplete="bday-month"
        inputMode="numeric"
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        ref={monthField}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
        data-dmy="month"
      />,
    ],
    [
      "year",
      <input
        name="dobyear"
        value={currentValueSplit[0] || ""}
        size={4}
        maxLength={4}
        placeholder={config.yearPlaceholderText}
        spellCheck="false"
        autoComplete="bday-year"
        inputMode="numeric"
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        ref={yearField}
        aria-required={required}
        aria-invalid={(anyErrors && isDirty) || undefined}
        aria-describedby={anyErrors ? errorId : undefined}
        data-dmy="year"
      />,
    ],
  ]);

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      <div className="combinedField textField">
        {orderedFields.map((field, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${field.type}_${index}`}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {field.type === "literal" ? (
              <span className="fieldSeparator">{field.value}</span>
            ) : fields.has(field.type) ? (
              fields.get(field.type)
            ) : null}
          </Fragment>
        ))}
      </div>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(valueFromStore)}
          />
        ) : null}
      </div>
    </LabelledField>
  );
}
